/**
 * Authentication Types
 * 
 * Core types for authentication and user management
 * shared across admin-console and agent-ui.
 */

import { Session, Identity } from '@ory/client';

/**
 * Authenticated user information
 */
export interface User {
  id: string;
  email: string;
  name?: string;
  avatar?: string;
  roles: string[];
  traits: Record<string, unknown>;
}

/**
 * Authenticator Assurance Level
 * - aal0: No authentication
 * - aal1: Password only
 * - aal2: Password + MFA (TOTP)
 * - aal3: Hardware security key
 */
export type AuthenticatorAssuranceLevel = 'aal0' | 'aal1' | 'aal2' | 'aal3';

/**
 * Authentication state
 */
export interface AuthState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is being loaded */
  isLoading: boolean;
  /** Current user (null if not authenticated) */
  user: User | null;
  /** Ory session object */
  session: Session | null;
  /** Authentication error */
  error: Error | null;
  /** Current authenticator assurance level */
  aal: AuthenticatorAssuranceLevel | null;
  /** Whether MFA is configured for this user */
  hasMfaConfigured: boolean;
}

/**
 * Authentication context value with methods
 */
export interface AuthContextValue extends AuthState {
  /** Redirect to login page */
  login: (returnTo?: string) => void;
  /** Perform logout */
  logout: () => Promise<void>;
  /** Redirect to registration page */
  register: (returnTo?: string) => void;
  /** Refresh session */
  refresh: () => Promise<void>;
  /** Check if session meets required AAL */
  hasRequiredAal: (required: AuthenticatorAssuranceLevel) => boolean;
}

/**
 * TOTP Setup data extracted from Kratos flow
 */
export interface TotpSetupData {
  /** Base32 encoded TOTP secret */
  secret: string;
  /** QR code image URL */
  qrUrl: string;
}

/**
 * MFA setup page props
 */
export interface MfaSetupPageProps {
  /** Called when MFA setup completes successfully */
  onComplete: (recoveryCodes: string[]) => void;
  /** Called when user cancels MFA setup */
  onCancel?: () => void;
}

/**
 * Recovery codes page props
 */
export interface RecoveryCodesPageProps {
  /** Recovery codes to display */
  codes: string[];
  /** Called when user confirms they've saved codes */
  onComplete: () => void;
}

/**
 * MFA challenge page props
 */
export interface MfaChallengePageProps {
  /** Called when MFA verification succeeds */
  onSuccess: () => void;
  /** Called when user cancels MFA challenge */
  onCancel?: () => void;
  /** URL to return to after successful verification */
  returnUrl?: string;
}

/**
 * Extract user info from Ory identity
 */
export function identityToUser(identity: Identity): User {
  const traits = (identity.traits as Record<string, unknown>) || {};

  // Kratos name can be a string or an object {first, last}
  let name: string | undefined;
  if (typeof traits.name === 'string') {
    name = traits.name;
  } else if (traits.name && typeof traits.name === 'object') {
    const n = traits.name as { first?: string; last?: string };
    name = [n.first, n.last].filter(Boolean).join(' ') || undefined;
  }
  if (!name) {
    name = (traits.username as string) || undefined;
  }

  return {
    id: identity.id,
    email: (traits.email as string) || '',
    name,
    avatar: (traits.avatar as string) || undefined,
    roles: (identity.metadata_public as { roles?: string[] })?.roles || ['user'],
    traits,
  };
}

/**
 * Check if session has MFA configured (TOTP or recovery codes)
 */
export function hasMfaConfigured(session: Session): boolean {
  if (!session.authentication_methods) return false;
  
  return session.authentication_methods.some(
    method => method.method === 'totp' || method.method === 'lookup_secret'
  );
}

/**
 * Check if the session was created via an external IdP (federated SSO).
 *
 * Federated identities get MFA from the identity provider (e.g. Entra ID
 * Conditional Access). Kratos AAL2 is unreachable for them (no local TOTP),
 * so MFA-guards must not bounce these sessions to a Kratos challenge
 * (ISS-285).
 */
export function isFederatedSession(session: Session): boolean {
  if (!session.authentication_methods) return false;
  
  return session.authentication_methods.some(
    method => method.method === 'oidc'
  );
}

/**
 * Get the AAL from session
 */
export function getAal(session: Session): AuthenticatorAssuranceLevel | null {
  return (session.authenticator_assurance_level as AuthenticatorAssuranceLevel) || null;
}
